import { Box, CircularProgress } from '@mui/material';
import PropTypes from 'prop-types';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Sector,
  Tooltip,
} from 'recharts';

const GS = {
  teal: '#4A7C8E',
  textMuted: '#6B7280',
  white: '#ffffff',
  border: '#E5E7EB',
};

const tooltipContentStyle = {
  borderRadius: 12,
  border: `1px solid ${GS.border}`,
  backgroundColor: GS.white,
  padding: '12px 16px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
};

const PieChartTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    const total = payload.reduce((acc, p) => acc + (p.value || 0), 0);
    return (
      <div style={tooltipContentStyle}>
        {payload.map((p) => (
          <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, fontSize: 12 }}>
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: 4,
                backgroundColor: p.payload?.fill ?? p.color,
              }}
            />
            <span style={{ color: GS.textMuted, minWidth: 90 }}>{p.name}:</span>
            <span style={{ fontWeight: 700, color: '#1E3A45' }}>{p.value?.toLocaleString()}</span>
            <span style={{ color: '#9CA3AF', fontSize: 11 }}>
              ({total ? ((p.value / total) * 100).toFixed(1) : 0}%)
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

PieChartTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.number,
      color: PropTypes.string,
      payload: PropTypes.shape({
        fill: PropTypes.string,
      }),
    })
  ),
};

function renderCustomerMixSector(props) {
  const fill = props.payload?.fill ?? props.fill;
  return <Sector {...props} fill={fill} stroke={GS.white} strokeWidth={2} />;
}

export default function CustomerMixPieChart({
  overviewLoading,
  customerMixTotal,
  customerPieData,
  activePieIndex,
  onPieEnter,
  onPieLeave,
}) {
  if (overviewLoading) {
    return <CircularProgress size={32} sx={{ color: GS.teal }} />;
  }

  if (customerMixTotal === 0) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: GS.textMuted,
          fontSize: 13,
          textAlign: 'center',
          px: 1,
        }}
      >
        —
      </Box>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={customerPieData}
          cx="50%"
          cy="50%"
          innerRadius={45}
          outerRadius={70}
          paddingAngle={2}
          dataKey="value"
          animationDuration={800}
          animationEasing="ease-out"
          activeIndex={activePieIndex}
          activeShape={{ outerRadius: 78 }}
          onMouseEnter={onPieEnter}
          onMouseLeave={onPieLeave}
          shape={renderCustomerMixSector}
        />
        <Tooltip content={<PieChartTooltip />} animationDuration={200} animationEasing="ease-out" />
      </PieChart>
    </ResponsiveContainer>
  );
}

CustomerMixPieChart.propTypes = {
  overviewLoading: PropTypes.bool.isRequired,
  customerMixTotal: PropTypes.number.isRequired,
  customerPieData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      fill: PropTypes.string.isRequired,
    })
  ).isRequired,
  activePieIndex: PropTypes.number.isRequired,
  onPieEnter: PropTypes.func.isRequired,
  onPieLeave: PropTypes.func.isRequired,
};
